import { useEffect, useState } from "react";
import axios from "axios";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  Tooltip, XAxis, YAxis, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";
import DashboardLayout from "../layout/DashboardLayout";
import "../layout/Dashboard.css";
import "./AdvancedAnalyticsPage.css";

const API_BASE = import.meta.env.VITE_API_URL ?? "http://localhost:5050";

const CHART_TOOLTIP = {
  contentStyle: { background: "#0E1E38", border: "1px solid #e2e8f0", borderRadius: 10, fontSize: 12, color: "#F2EBD9", boxShadow: "0 4px 16px rgba(0,0,0,0.1)" },
  labelStyle: { color: "rgba(242,235,217,0.4)" },
};
const TICK  = { fontSize: 11, fill: "#475569" };
const GRID  = { strokeDasharray: "3 3", stroke: "rgba(255,255,255,0.04)" };
const PIE_COLORS = ["#6366f1","#10b981","#f59e0b","#ef4444","#06b6d4","#8b5cf6","#ec4899"];

function occupancyColor(pct: number) {
  if (pct >= 80) return "#ef4444";
  if (pct >= 60) return "#f59e0b";
  if (pct >= 40) return "#6366f1";
  if (pct >= 20) return "#10b981";
  return "#334155";
}

interface Analytics {
  ADRthisMonth: number;
  ADRlastMonth: number;
  ADRallTime: number;
  RevPARthisMonth: number;
  RevPARlastMonth: number;
  revparByMonth: { month: string; revpar: number; adr: number; revenue: number }[];
  bySource: { source: string; count: number; revenue: number }[];
  forecast: { date: string; occupancy: number; bookedRooms: number; totalRooms: number }[];
  totalRooms: number;
}

function KPICard({ label, value, sub, sublabel, up }: { label: string; value: string; sub?: string; sublabel?: string; up?: boolean | null }) {
  return (
    <div className="aap-kpi-card">
      <div className="aap-kpi-label">{label}</div>
      <div className="aap-kpi-value">{value}</div>
      {sub !== undefined && (
        <div className={`aap-kpi-sub ${up === true ? "up" : up === false ? "down" : ""}`}>
          {up === true ? "↑" : up === false ? "↓" : ""} {sub}
          {sublabel && <span className="aap-kpi-sublabel"> {sublabel}</span>}
        </div>
      )}
    </div>
  );
}

function delta(curr: number, prev: number) {
  if (prev === 0) return null;
  const pct = Math.round(((curr - prev) / prev) * 100);
  return { pct, up: pct >= 0 };
}

export default function AdvancedAnalyticsPage() {
  const [data, setData]       = useState<Analytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");

  const token      = localStorage.getItem("ai2_token") ?? "";
  const hotel      = localStorage.getItem("ai2_hotel") ?? "";
  const authHeaders = { Authorization: `Bearer ${token}`, "x-hotel": hotel };

  useEffect(() => {
    axios.get(`${API_BASE}/api/dashboard/advanced-analytics`, { headers: authHeaders })
      .then(res => setData(res.data))
      .catch(err => setError(err?.response?.data?.error ?? err?.message ?? "Failed to load analytics"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <DashboardLayout>
      <div className="aap-loading">Loading analytics…</div>
    </DashboardLayout>
  );

  if (error || !data) return (
    <DashboardLayout>
      <div className="aap-error">{error || "No data"}</div>
    </DashboardLayout>
  );

  const adrDelta    = delta(data.ADRthisMonth, data.ADRlastMonth);
  const revparDelta = delta(data.RevPARthisMonth, data.RevPARlastMonth);

  const totalSourceRevenue = data.bySource.reduce((s, x) => s + x.revenue, 0);
  const totalSourceCount   = data.bySource.reduce((s, x) => s + x.count, 0);

  // Forecast: group into weeks for readability
  const forecastWeekly = data.forecast.filter((_,i) => i % 3 === 0);

  return (
    <DashboardLayout>
      <div className="aap-page">

        {/* Header */}
        <div className="aap-header">
          <div>
            <h1 className="aap-title">Advanced Analytics</h1>
            <p className="aap-subtitle">RevPAR · ADR · Forecasting · Source Breakdown</p>
          </div>
        </div>

        {/* KPI Row */}
        <div className="aap-kpi-row">
          <KPICard
            label="RevPAR — This Month"
            value={`EGP ${data.RevPARthisMonth.toLocaleString()}`}
            sub={revparDelta ? `${revparDelta.pct > 0 ? "+" : ""}${revparDelta.pct}% vs last month` : "No comparison"}
            up={revparDelta?.up}
          />
          <KPICard
            label="RevPAR — Last Month"
            value={`EGP ${data.RevPARlastMonth.toLocaleString()}`}
          />
          <KPICard
            label="ADR — This Month"
            value={`EGP ${data.ADRthisMonth.toLocaleString()}`}
            sub={adrDelta ? `${adrDelta.pct > 0 ? "+" : ""}${adrDelta.pct}% vs last month` : "No comparison"}
            up={adrDelta?.up}
          />
          <KPICard
            label="ADR — Last Month"
            value={`EGP ${data.ADRlastMonth.toLocaleString()}`}
          />
          <KPICard
            label="ADR — All Time"
            value={`EGP ${data.ADRallTime.toLocaleString()}`}
          />
          <KPICard
            label="Total Rooms"
            value={String(data.totalRooms)}
            sub="in inventory"
          />
        </div>

        {/* RevPAR & ADR trend */}
        <div className="aap-section">
          <div className="aap-section-title">RevPAR & ADR — 12-Month Trend</div>
          <div className="aap-chart-box">
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data.revparByMonth} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid {...GRID} />
                <XAxis dataKey="month" tick={TICK} />
                <YAxis tick={TICK} />
                <Tooltip {...CHART_TOOLTIP} formatter={(v: number) => [`EGP ${v.toLocaleString()}`, ""]} />
                <Legend wrapperStyle={{ fontSize: 11, color: "rgba(242,235,217,0.4)" }} />
                <Bar dataKey="revpar" name="RevPAR" fill="#6366f1" radius={[4,4,0,0]} />
                <Bar dataKey="adr"    name="ADR"    fill="#10b981" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Source breakdown + Forecast side by side */}
        <div className="aap-two-col">

          {/* Source breakdown */}
          <div className="aap-section">
            <div className="aap-section-title">Booking Source Breakdown</div>
            <div className="aap-chart-box">
              <div style={{ display: "flex", gap: 24, alignItems: "center" }}>
                <ResponsiveContainer width={200} height={200}>
                  <PieChart>
                    <Pie
                      data={data.bySource}
                      dataKey="revenue"
                      nameKey="source"
                      cx="50%"
                      cy="50%"
                      outerRadius={85}
                      innerRadius={45}
                    >
                      {data.bySource.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      {...CHART_TOOLTIP}
                      formatter={(v: number) => [`EGP ${v.toLocaleString()}`, "Revenue"]}
                    />
                  </PieChart>
                </ResponsiveContainer>

                <div className="aap-source-table">
                  {data.bySource.map((s, i) => (
                    <div key={s.source} className="aap-source-row">
                      <div className="aap-source-dot" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                      <div className="aap-source-name">{s.source}</div>
                      <div className="aap-source-count">{s.count} bkgs</div>
                      <div className="aap-source-rev">EGP {s.revenue.toLocaleString()}</div>
                      <div className="aap-source-pct">
                        {totalSourceRevenue > 0 ? Math.round((s.revenue / totalSourceRevenue) * 100) : 0}%
                      </div>
                    </div>
                  ))}
                  {data.bySource.length === 0 && (
                    <div style={{ color: "rgba(242,235,217,0.45)", fontSize: 13, padding: 12 }}>
                      No source data — add booking source when creating bookings
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Quick stats */}
          <div className="aap-section">
            <div className="aap-section-title">Source Revenue Summary</div>
            <div className="aap-chart-box" style={{ padding: "16px 0" }}>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart
                  data={data.bySource}
                  layout="vertical"
                  margin={{ top: 0, right: 20, left: 10, bottom: 0 }}
                >
                  <CartesianGrid {...GRID} horizontal={false} />
                  <XAxis type="number" tick={TICK} tickFormatter={(v) => `${Math.round(v/1000)}k`} />
                  <YAxis type="category" dataKey="source" tick={{ ...TICK, fontSize: 11 }} width={80} />
                  <Tooltip {...CHART_TOOLTIP} formatter={(v: number) => [`EGP ${v.toLocaleString()}`, "Revenue"]} />
                  <Bar dataKey="revenue" name="Revenue" radius={[0,4,4,0]}>
                    {data.bySource.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* 60-day occupancy forecast */}
        <div className="aap-section">
          <div className="aap-section-title">60-Day Occupancy Forecast</div>
          <div className="aap-chart-box">
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={forecastWeekly} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="occGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#6366f1" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid {...GRID} />
                <XAxis dataKey="date" tick={TICK} tickFormatter={d => d.slice(5)} />
                <YAxis tick={TICK} domain={[0, 100]} tickFormatter={v => `${v}%`} />
                <Tooltip
                  {...CHART_TOOLTIP}
                  formatter={(v: number, name: string) => [
                    name === "occupancy" ? `${v}%` : v,
                    name === "occupancy" ? "Occupancy" : name
                  ]}
                />
                <Area
                  type="monotone" dataKey="occupancy" name="occupancy"
                  stroke="#6366f1" strokeWidth={2}
                  fill="url(#occGrad)"
                />
              </AreaChart>
            </ResponsiveContainer>

            {/* Heatmap-style row */}
            <div className="aap-forecast-grid">
              {data.forecast.map(d => (
                <div
                  key={d.date}
                  className="aap-forecast-cell"
                  style={{ background: occupancyColor(d.occupancy) }}
                  title={`${d.date}: ${d.occupancy}% (${d.bookedRooms}/${d.totalRooms} rooms)`}
                />
              ))}
            </div>
            <div style={{ display: "flex", gap: 16, justifyContent: "center", marginTop: 8, fontSize: 11, color: "rgba(242,235,217,0.45)" }}>
              <span style={{ color: "rgba(242,235,217,0.35)" }}>■ 0–19%</span>
              <span style={{ color: "#10b981" }}>■ 20–39%</span>
              <span style={{ color: "#6366f1" }}>■ 40–59%</span>
              <span style={{ color: "#f59e0b" }}>■ 60–79%</span>
              <span style={{ color: "#ef4444" }}>■ 80–100%</span>
            </div>
          </div>
        </div>

      </div>
    </DashboardLayout>
  );
}
